import type { RefObject } from 'react'
import { gsap, SplitText, useGSAP, registerMotion, NO_PREF } from './gsap'

/**
 * `[data-split-lines]` paragraphs (the manifesto lead) rise line by line out
 * of a mask when they reach 80 % of the viewport, once.
 *
 * - `autoSplit` re-splits on resize / font load; returning the tween from
 *   `onSplit` lets SplitText revert and time-sync it on each re-split.
 * - `aria: 'none'`: a lines-only split keeps every word intact, so the text
 *   reads fine as it is. The default ('auto') would move it into an
 *   `aria-label` on the <p> — a name ARIA prohibits on paragraphs, which
 *   screen readers skip — while hiding the lines.
 * - Once the reveal completes the split is reverted: the paragraph is plain
 *   text again (no line masks left to clip glyphs or to re-split).
 *
 * Reduced motion: nothing — the text is visible by default (not in the
 * anti-flash CSS).
 */
export function useSplitLines(scope: RefObject<HTMLElement | null>) {
  useGSAP(
    () => {
      registerMotion()
      if (!scope.current?.querySelector('[data-split-lines]')) return
      const mm = gsap.matchMedia()
      mm.add(NO_PREF, () => {
        gsap.utils.toArray<HTMLElement>('[data-split-lines]').forEach((el) => {
          SplitText.create(el, {
            type: 'lines',
            mask: 'lines',
            autoSplit: true,
            aria: 'none',
            onSplit: (self) =>
              gsap.from(self.lines, {
                yPercent: 100,
                autoAlpha: 0,
                stagger: 0.08,
                duration: 0.9,
                ease: 'power3.out',
                scrollTrigger: { trigger: el, start: 'top 80%', once: true },
                onComplete: () => {
                  self.revert()
                },
              }),
          })
        })
      })
    },
    { scope },
  )
}
